import React, { useState } from 'react';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import SelectLogin, { LoginMethod } from './SelectLogin';
import Pending from './Pending';
import OntoScan from './OntoScan';
import { ScoreResult } from '../../../types/api';
import Cyano from '../../../utils/DApp/Cyano';
import { useGlobalState } from '../../../store';
import Onto from '../../../utils/DApp/Onto';

enum Step {
  Select,
  Scan,
  Loading,
}

type Props = {
  onLoginSuccess: (ontId: string) => void;
}

const ConnectWallet: React.FC<Props> = ({ onLoginSuccess }) => {
  const [step, setStep] = useState<Step>(Step.Select);
  const { t } = useTranslation();
  const setDApp = useGlobalState('dApp')[1];
  const setAddressList = useGlobalState('addressList')[1];
  const setScoreLoading = useGlobalState('scoreLoading')[1];

  const onSelect = async (method: LoginMethod) => {
    if (method === LoginMethod.Onto) {
      setStep(Step.Scan);
    } else {
      setStep(Step.Loading);
      try {
        const ontIdResult = await Cyano.getOntIdWithCyano();
        const cyano = new Cyano(ontIdResult);
        cyano.setJwtStr(await cyano.getAddressJwt());
        cyano.setAddressListWithJwt();
        setScoreLoading(true);
        cyano.fetchScore().finally(() => {
          setScoreLoading(false);
        }); // not await
        setDApp(cyano);
        setAddressList(cyano.addressList);
        onLoginSuccess(ontIdResult);
      } catch (e) {
        message.error(e.message);
        setStep(Step.Select);
      }
    }
  };

  /**
   * init onto dapp after scan success
   * @param result
   */
  const onScanSuccess = (result: ScoreResult) => {
    const onto = new Onto(result.ontId);
    onto.setJwtStr(result.addressCredential)
      .setScoreResultId(result.oscoreId)
      .setAddressListWithJwt();
    setScoreLoading(true);
    onto.fetchScore().finally(() => {
      setScoreLoading(false);
    });
    setDApp(onto);
    setAddressList(onto.addressList);
    message.success(t('connect_success'));
    onLoginSuccess(onto.ontId);
  };

  const onScanFail = (result: ScoreResult) => {
    message.error(result.errorInfo);
    setStep(Step.Select);
  };

  if (step === Step.Select) {
    return <SelectLogin onSelect={onSelect} />;
  }
  if (step === Step.Scan) {
    return (
      <OntoScan
        onBack={() => setStep(Step.Select)}
        onSuccess={onScanSuccess}
        onFail={onScanFail}
      />
    );
  }
  if (step === Step.Loading) {
    return <Pending />;
  }
  return null;
};

export default ConnectWallet;
